import React from "react";
import { useTranslation } from "react-i18next";

// ─── 3D-style icon SVGs ───────────────────────────────────────────────────────
// Each icon is a CSS-3D sphere with a gradient + inner SVG path
const FEATURES = [
  {
    id: 1,
    gradient: ['#8258c8', '#5a3d9a'],
    glow: '#8258c8',
    emoji: '🧠',
    title: "AI & Machine Learning",
    desc: "Developing intelligent models for prediction, computer vision, natural language processing, and automated decision-making systems.",
  },
  {
    id: 2,
    gradient: ['#45d0bd', '#2a9e8d'],
    glow: '#45d0bd',
    emoji: '📱',
    title: "Mobile Application Engineering",
    desc: "High-performance Android and iOS applications built on reliability, scalability, and a seamless user experience.",
  },
  {
    id: 3,
    gradient: ['#2c84c8', '#1a5a96'],
    glow: '#2c84c8',
    emoji: '🌐',
    title: "Web & Cloud Platforms",
    desc: "Secure and scalable web systems, control panels, and cloud backends that manage complex workflows in organisations.",
  },
  {
    id: 4,
    gradient: ['#c8824a', '#a05e2e'],
    glow: '#c87d2c',
    emoji: '⚙️',
    title: "Automation Systems",
    desc: "Automating business processes, improving workflow efficiency, and coordinating intelligent systems across operations.",
  },
  {
    id: 5,
    gradient: ['#45d0bd', '#8258c8'],
    glow: '#45b0a8',
    emoji: '🦾',
    title: "IoT & Robotics Integration",
    desc: "Connected devices, sensor networks, and robotic solutions that enable intelligent monitoring and real-time control.",
  },
  {
    id: 6,
    gradient: ['#8258c8', '#2c84c8'],
    glow: '#6a52c0',
    emoji: '🥽',
    title: "AR / VR Experiences",
    desc: "Immersive digital environments for training, therapy, visualisation, and active user interaction.",
  },
];

/* ─── 3D sphere icon ─────────────────────────────────────────────────────── */
const Icon3D = ({ gradient, glow, emoji }) => (
  <div style={{
    width: 80, height: 80,
    borderRadius: '50%',
    background: `radial-gradient(circle at 35% 35%, ${gradient[0]}, ${gradient[1]})`,
    boxShadow: `
      0 0 0 1px rgba(255,255,255,0.12),
      inset 0 2px 4px rgba(255,255,255,0.22),
      inset 0 -4px 8px rgba(0,0,0,0.3),
      0 12px 32px ${glow}55,
      0 4px 12px rgba(0,0,0,0.4)
    `,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '2rem',
    position: 'relative',
    flexShrink: 0,
    transform: 'perspective(200px) rotateX(5deg) rotateY(-5deg)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  }}>
    {/* Specular highlight */}
    <div style={{
      position: 'absolute',
      top: '12%', left: '18%',
      width: '36%', height: '28%',
      borderRadius: '50%',
      background: 'radial-gradient(ellipse, rgba(255,255,255,0.45) 0%, transparent 80%)',
      pointerEvents: 'none',
    }} />
    {emoji}
  </div>
);

const Feature = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  return (
    <section className="feature-sec1 ibt-section-gap">
      <div className="container">

        {/* Header */}
        <div className="sec-title text-center" style={{ marginBottom: 50 }}>
          <h2 className="title animated-heading">
            {t("Core Technologies")}
          </h2>
          <p style={{ color: '#6b6b88', maxWidth: 480, margin: '12px auto 0', fontSize: '0.95rem', lineHeight: 1.7 }}>
            {t("The engineering stack that powers every WIZ product — from edge AI to immersive XR.")}
          </p>
        </div>

        {/* Grid */}
        <div className={`row g-4 ${isRtl ? 'flex-row-reverse' : ''}`} style={{ justifyContent: 'center' }}>
          {FEATURES.map((f) => (
            <div key={f.id} className="col-lg-4 col-md-6 col-sm-12">
              <div
                className="feature-card"
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '36px 28px 30px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: '20px',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                }}
                onMouseEnter={e => {
                  const card = e.currentTarget;
                  card.style.transform = 'translateY(-6px)';
                  card.style.boxShadow = `0 20px 48px ${f.glow}20`;
                  card.style.borderColor = `${f.glow}30`;
                  // Tilt the icon
                  const icon = card.querySelector('.feat-icon-wrap');
                  if (icon) icon.style.transform = 'perspective(200px) rotateX(-5deg) rotateY(8deg) scale(1.08)';
                }}
                onMouseLeave={e => {
                  const card = e.currentTarget;
                  card.style.transform = '';
                  card.style.boxShadow = '';
                  card.style.borderColor = 'rgba(255,255,255,0.07)';
                  const icon = card.querySelector('.feat-icon-wrap');
                  if (icon) icon.style.transform = 'perspective(200px) rotateX(5deg) rotateY(-5deg)';
                }}
              >
                {/* 3D Icon */}
                <div
                  className="feat-icon-wrap"
                  style={{
                    marginBottom: 22,
                    width: 80, height: 80,
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 35%, ${f.gradient[0]}, ${f.gradient[1]})`,
                    boxShadow: `
                      0 0 0 1px rgba(255,255,255,0.12),
                      inset 0 2px 4px rgba(255,255,255,0.22),
                      inset 0 -4px 8px rgba(0,0,0,0.3),
                      0 12px 32px ${f.glow}55,
                      0 4px 12px rgba(0,0,0,0.4)
                    `,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '2rem',
                    position: 'relative',
                    transform: 'perspective(200px) rotateX(5deg) rotateY(-5deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  {/* Specular highlight blob */}
                  <div style={{
                    position: 'absolute',
                    top: '12%', left: '18%',
                    width: '36%', height: '28%',
                    borderRadius: '50%',
                    background: 'radial-gradient(ellipse, rgba(255,255,255,0.45) 0%, transparent 80%)',
                    pointerEvents: 'none',
                  }} />
                  {f.emoji}
                </div>

                <h4 className="title" style={{
                  color: '#e8eaf6', marginBottom: 12,
                  fontSize: '1.05rem', fontWeight: 700,
                }}>
                  {t(f.title)}
                </h4>
                <p style={{ color: '#6b6b88', fontSize: '14px', lineHeight: 1.72, margin: 0 }}>
                  {t(f.desc)}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Feature;
